'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import { color, spacing, borderRadius, breakpoints } from '@/theme/tokens';
import IconChevronDown from '@/assets/icons/IconChevronDown';

export interface ExpandableSectionMaxHeight {
    desktop?: number | string;
    tablet?: number | string;
    mobile?: number | string;
}

interface ExpandableSectionProps {
    title: string;
    children: React.ReactNode;
    defaultExpanded?: boolean;
    maxHeight?: ExpandableSectionMaxHeight;
}

function toMaxHeightValue(value: number | string): string {
    return typeof value === 'number' ? `${value}px` : value;
}

export default function ExpandableSection({
    title,
    children,
    defaultExpanded = false,
    maxHeight,
}: ExpandableSectionProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [contentHeight, setContentHeight] = useState<number>(0);
    const innerContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (innerContentRef.current) {
            setContentHeight(innerContentRef.current.scrollHeight);
        }
    }, [children]);

    useEffect(() => {
        const handleResize = () => {
            if (innerContentRef.current) {
                setContentHeight(innerContentRef.current.scrollHeight);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const hasMaxHeight =
        maxHeight && (maxHeight.desktop != null || maxHeight.tablet != null || maxHeight.mobile != null);

    return (
        <Box
            sx={{
                borderRadius: borderRadius[8],
                overflow: 'hidden',
                backgroundColor: color.white[0],
                display: 'flex',
                flexDirection: 'column',
                '&:has(button:focus-visible)': {
                    outline: `2px solid ${color.gray[80]}`,
                    outlineOffset: '2px',
                },
                ...(hasMaxHeight && {
                    maxHeight: expanded
                        ? maxHeight?.desktop != null
                            ? toMaxHeightValue(maxHeight.desktop)
                            : undefined
                        : undefined,
                    [`@media (max-width: ${breakpoints.md})`]: {
                        maxHeight: expanded
                            ? maxHeight?.tablet != null
                                ? toMaxHeightValue(maxHeight.tablet)
                                : undefined
                            : undefined,
                    },
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        maxHeight: expanded
                            ? maxHeight?.mobile != null
                                ? toMaxHeightValue(maxHeight.mobile)
                                : undefined
                            : undefined,
                    },
                }),
            }}
        >
            <Box
                component="button"
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
                aria-controls="expandable-section-content"
                id="expandable-section-header"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minHeight: 56,
                    padding: spacing[32],
                    backgroundColor: color.white[0],
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    flexShrink: 0,
                    '&:focus-visible': {
                        outline: 'none',
                    },

                    [`@media (max-width: ${breakpoints.md})`]: {
                        padding: spacing[24],
                    },

                    [`@media (max-width: ${breakpoints.sm})`]: {
                        padding: spacing[16],
                    },
                }}
            >
                <Typography variant="heading2bold" color={color.gray[80]} component="h3" sx={{ flex: 1 }}>
                    {title}
                </Typography>
                <Box
                    sx={{
                        color: color.gray[70],
                        marginLeft: spacing[16],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.3s ease',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                >
                    <IconChevronDown color={color.gray[70]} width={24} height={24} />
                </Box>
            </Box>

            <Box
                id="expandable-section-content"
                aria-labelledby="expandable-section-header"
                sx={{
                    transition: 'max-height 0.3s ease',
                    maxHeight: expanded ? contentHeight : 0,
                    overflow: expanded && hasMaxHeight ? 'auto' : 'hidden',
                    ...(hasMaxHeight &&
                        expanded && {
                            flex: '1 1 auto',
                            minHeight: 0,
                            maxHeight: 'none',
                        }),
                }}
            >
                <Box
                    ref={innerContentRef}
                    sx={{
                        padding: `0 ${spacing[32]} ${spacing[32]} ${spacing[32]}`,
                        [`@media (max-width: ${breakpoints.md})`]: {
                            padding: `0 ${spacing[24]} ${spacing[24]} ${spacing[24]}`,
                        },
                        [`@media (max-width: ${breakpoints.sm})`]: {
                            padding: `0 ${spacing[16]} ${spacing[16]} ${spacing[16]}`,
                        },
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
}
